// Routes-mode hook for the Logistics bucket. Renamed + moved from
// src/components/manufacturing/useManufacturingDeliveryRoutesTab.ts when
// the logistics bucket was extracted -- shape and behaviour are
// unchanged; only the location and the navTick / newSignal naming
// follow the new "logistics" word.
import { useCallback, useEffect, useState } from "react";
import {
  createLogisticsRoute,
  listLogisticsCarrierRigs,
  listLogisticsCarriers,
  listLogisticsRoutes,
  listLogisticsUnits,
  type LogiCarrier,
  type LogiCarrierRig,
  type LogiRoute,
  type LogiRouteState,
  type LogiStopKind,
} from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturing/types";
import {
  DEFAULT_DRAFT,
  deriveSchedulingFromDraft,
  type SchedulingDraft,
  type SchedulingMode,
} from "@/lib/manufacturing/scheduledForPicker";

const ROUTE_STATES: LogiRouteState[] = [
  "planned",
  "scheduled",
  "in_transit",
  "delivered",
  "cancelled",
];

export type LogisticsRoutesListFilter = {
  unit_id: string;
  carrier_id: string;
  route_state: string;
  scheduled_on: string;
  scheduled_window_label: string;
  origin_stop_kind: string;
};

export function useLogisticsRoutesPanel(navTick: number, newSignal: number) {
  const [routes, setRoutes] = useState<LogiRoute[]>([]);
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [carriers, setCarriers] = useState<LogiCarrier[]>([]);
  // D3j: rigs for the currently-picked carrier on the create form.
  // Lazy-loaded the first time a carrier is chosen, then re-fetched on
  // every carrier change. Empty array also covers the "carrier-tentative"
  // case (no carrier picked yet -> rig picker hidden / disabled).
  const [createCarrierRigs, setCreateCarrierRigs] = useState<LogiCarrierRig[]>([]);
  const [createRigsLoading, setCreateRigsLoading] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [filter, setFilter] = useState<LogisticsRoutesListFilter>({
    unit_id: "",
    carrier_id: "",
    route_state: "",
    scheduled_on: "",
    scheduled_window_label: "",
    origin_stop_kind: "",
  });

  // Create form state. carrier_id stays optional ("carrier-tentative" routes
  // are a first-class concept per inventory_host_replacement_plan.md §9).
  // D3j: rig_id + driver_name are also optional. Driver auto-fills from the
  // selected rig's standard_driver_name on rig selection (see setNewRigId
  // below) but is always overrideable for last-minute swaps.
  const [creating, setCreating] = useState(false);
  const [newUnitId, setNewUnitId] = useState("");
  const [newCarrierId, setNewCarrierId] = useState("");
  const [newRigId, setNewRigIdState] = useState("");
  const [newDriverName, setNewDriverName] = useState("");
  // D3k: scheduling state is now a structured draft (mode + date +
  // start/end time) instead of a single ISO text field. Derivation to
  // (scheduled_for, scheduled_window_label) happens in submitCreate via
  // deriveSchedulingFromDraft. Empty date = "no schedule yet" (route
  // sits in planned without a scheduled_for, identical to today's
  // behavior when the user leaves the field blank).
  const [newSchedulingDraft, setNewSchedulingDraft] = useState<SchedulingDraft>(DEFAULT_DRAFT);
  const [newNotes, setNewNotes] = useState("");

  const reloadUnits = useCallback(async () => {
    const res = await listLogisticsUnits();
    if (!res.ok) {
      // Non-fatal for the routes list; just leaves the picker empty.
      setUnits([]);
      return;
    }
    setUnits(Array.isArray(res.data.units) ? res.data.units : []);
  }, []);

  const reloadCarriers = useCallback(async () => {
    // Active carriers only -- inactive ones shouldn't pollute the picker. The
    // carriers tab (forthcoming, also in the logistics bucket) can still
    // surface them via is_active=false filter.
    const res = await listLogisticsCarriers({ is_active: true });
    if (!res.ok) {
      setCarriers([]);
      return;
    }
    setCarriers(Array.isArray(res.data.carriers) ? res.data.carriers : []);
  }, []);

  const reloadRoutes = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listLogisticsRoutes({
      unit_id: filter.unit_id || undefined,
      carrier_id: filter.carrier_id || undefined,
      route_state: (filter.route_state as LogiRouteState) || undefined,
      scheduled_on: filter.scheduled_on || undefined,
      scheduled_window_label: filter.scheduled_window_label || undefined,
      origin_stop_kind: (filter.origin_stop_kind as LogiStopKind) || undefined,
    });
    setListBusy(false);
    if (!res.ok) {
      setRoutes([]);
      setLocalError(res.error);
      return;
    }
    setRoutes(Array.isArray(res.data.routes) ? res.data.routes : []);
  }, [
    filter.unit_id,
    filter.carrier_id,
    filter.route_state,
    filter.scheduled_on,
    filter.scheduled_window_label,
    filter.origin_stop_kind,
  ]);

  useEffect(() => {
    void reloadUnits();
    void reloadCarriers();
  }, [reloadUnits, reloadCarriers, navTick]);

  useEffect(() => {
    void reloadRoutes();
  }, [reloadRoutes, navTick]);

  useEffect(() => {
    if (newSignal === 0) return;
    setCreating(true);
    setLocalError(null);
    setNewUnitId("");
    setNewCarrierId("");
    setNewRigIdState("");
    setNewDriverName("");
    setCreateCarrierRigs([]);
    setNewSchedulingDraft(DEFAULT_DRAFT);
    setNewNotes("");
  }, [newSignal]);

  // D3j: re-fetch the rig picker options whenever the carrier selection
  // changes. Clearing the carrier clears the picker too. Inactive rigs
  // are excluded so the picker doesn't show parked rigs as available.
  useEffect(() => {
    const carrierId = newCarrierId.trim();
    if (!carrierId) {
      setCreateCarrierRigs([]);
      return;
    }
    let cancelled = false;
    setCreateRigsLoading(true);
    void (async () => {
      const res = await listLogisticsCarrierRigs({
        carrier_id: carrierId,
        is_active: true,
      });
      if (cancelled) return;
      setCreateRigsLoading(false);
      if (!res.ok) {
        setCreateCarrierRigs([]);
        return;
      }
      const list = Array.isArray(res.data.rigs) ? res.data.rigs : [];
      setCreateCarrierRigs([...list].sort((a, b) => a.designation.localeCompare(b.designation)));
    })();
    return () => {
      cancelled = true;
    };
  }, [newCarrierId]);

  // Setting the rig auto-fills the driver from the rig's standard driver,
  // but leaves it overridable for last-minute swaps. Empty rig selection
  // clears the auto-filled driver if it matches the previously-selected
  // rig's default; otherwise we leave whatever the user already typed.
  const setNewRigId = useCallback(
    (rigId: string) => {
      setNewRigIdState(rigId);
      if (!rigId) return;
      const picked = createCarrierRigs.find((r) => r.id === rigId);
      if (picked && picked.standard_driver_name) {
        // Only auto-fill if the field is currently blank, so the user's
        // manual override isn't clobbered when they re-pick a rig.
        setNewDriverName((prev) => prev.trim() || picked.standard_driver_name!);
      }
    },
    [createCarrierRigs]
  );

  // Changing the carrier clears the rig (a rig from carrier A doesn't
  // belong to carrier B) so the backend's cross-FK guard never trips.
  const setNewCarrierIdWithReset = useCallback((id: string) => {
    setNewCarrierId(id);
    setNewRigIdState("");
  }, []);

  const cancelCreate = useCallback(() => {
    setCreating(false);
    setLocalError(null);
  }, []);

  // D3k: small typed setters that mutate the scheduling draft in place
  // (the form binds individual fields and we want isolated re-renders).
  const setNewScheduleMode = useCallback((mode: SchedulingMode) => {
    setNewSchedulingDraft((d) => ({ ...d, mode }));
  }, []);
  const setNewScheduleDate = useCallback((date: string) => {
    setNewSchedulingDraft((d) => ({ ...d, date }));
  }, []);
  const setNewScheduleStartTime = useCallback((startTime: string) => {
    setNewSchedulingDraft((d) => ({ ...d, startTime }));
  }, []);
  const setNewScheduleEndTime = useCallback((endTime: string) => {
    setNewSchedulingDraft((d) => ({ ...d, endTime }));
  }, []);

  const submitCreate = useCallback(async () => {
    const unit_id = newUnitId.trim();
    if (!unit_id) {
      setLocalError("Unit is required.");
      return;
    }
    // D3k: derive the canonical (scheduled_for, scheduled_window_label)
    // from the picker draft. ok:true + value:null = no schedule (date
    // was left blank); ok:false = surface the picker's validation error
    // to the user without hitting the API.
    const derived = deriveSchedulingFromDraft(newSchedulingDraft);
    if (!derived.ok) {
      setLocalError(derived.error);
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createLogisticsRoute({
      unit_id,
      carrier_id: newCarrierId.trim() || undefined,
      rig_id: newRigId.trim() || undefined,
      driver_name: newDriverName.trim() || undefined,
      scheduled_for: derived.value?.scheduled_for ?? undefined,
      scheduled_window_label: derived.value?.scheduled_window_label ?? undefined,
      notes: newNotes.trim() || undefined,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreating(false);
    await reloadRoutes();
  }, [
    newUnitId,
    newCarrierId,
    newRigId,
    newDriverName,
    newSchedulingDraft,
    newNotes,
    reloadRoutes,
  ]);

  return {
    routes,
    units,
    carriers,
    createCarrierRigs,
    createRigsLoading,
    listBusy,
    actionBusy,
    localError,
    filter,
    setFilter,
    creating,
    newUnitId,
    setNewUnitId,
    newCarrierId,
    setNewCarrierId: setNewCarrierIdWithReset,
    newRigId,
    setNewRigId,
    newDriverName,
    setNewDriverName,
    // D3k: scheduling draft + typed setters. Form binds via these
    // setters; submitCreate derives the wire shape internally.
    newScheduleMode: newSchedulingDraft.mode,
    setNewScheduleMode,
    newScheduleDate: newSchedulingDraft.date,
    setNewScheduleDate,
    newScheduleStartTime: newSchedulingDraft.startTime,
    setNewScheduleStartTime,
    newScheduleEndTime: newSchedulingDraft.endTime,
    setNewScheduleEndTime,
    newNotes,
    setNewNotes,
    cancelCreate,
    submitCreate,
    reload: reloadRoutes,
    routeStateOptions: ROUTE_STATES,
  };
}
